/**
 * Contextual toolbar for file operations and metadata management
 */
import { useState, useMemo } from 'react';
import {
  Copy,
  Edit3,
  Trash2,
  FileText,
  Hash,
  Tag,
  Clock,
  FolderOpen,
  MoreHorizontal,
  AlertTriangle,
  Save,
  X,
  Check,
  History,
  FileType,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import type { File } from '@phrasewright/types';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import {
  useGetFilesQuery,
  useUpdateFileMutation,
  useDeleteFileMutation,
} from '@/store/api/unifiedStorageApi';
import {
  updateFile,
  deleteFile as deleteFileFromStore,
} from '@/store/slices/filesSlice';
import { DuplicateFileDialog } from './dialogs/DuplicateFileDialog';
import { RenameFileDialog } from './dialogs/RenameFileDialog';
import { DeleteFileDialog } from './dialogs/DeleteFileDialog';
import { TagEditor } from './TagEditor';
import { RevisionHistoryDialog } from './RevisionHistory';
import { FrontMatterEditor } from './FrontMatterEditor';
import { updateFrontmatter } from '@/core/utils/frontmatter';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

interface ContextualToolbarProps {
  file: File;
  onSave?: () => Promise<void>;
  onContentChange?: (content: string) => void;
}

export function ContextualToolbar({
  file,
  onSave,
  onContentChange,
}: ContextualToolbarProps) {
  const dispatch = useAppDispatch();
  const { currentProject } = useAppSelector((state) => state.project);
  const { files } = useAppSelector((state) => state.files);
  const isDirty = useAppSelector((state) => state.editor.isDirty);

  // Dialog states
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Tag editing state
  const [isEditingTags, setIsEditingTags] = useState(false);
  const [pendingTags, setPendingTags] = useState<string[]>(file.metadata.tags);

  // Type editing state - TODO: Implement type editing functionality

  // Storage API mutations
  const [updateFileApi, { isLoading: isUpdating }] = useUpdateFileMutation();
  const [deleteFileApi, { isLoading: isDeleting }] = useDeleteFileMutation();

  // Get all project files for tag autocomplete
  const { data: projectFiles = [] } = useGetFilesQuery(
    currentProject ? { projectId: currentProject.id } : undefined,
    { skip: !currentProject }
  );

  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    files.forEach((f) => f.metadata.tags.forEach((tag) => tagSet.add(tag)));
    projectFiles.forEach((f) => {
      const tags = f.tags || [];
      tags.forEach((tag) => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [files, projectFiles]);

  // Handle tag save
  const handleSaveTags = async () => {
    if (!currentProject) return;

    try {
      // Update the content with new frontmatter
      const updatedContent = updateFrontmatter(file.content, {
        tags: pendingTags,
      });

      const updatedFile = {
        ...file,
        content: updatedContent,
        metadata: {
          ...file.metadata,
          tags: pendingTags,
          updated: new Date().toISOString(),
        },
      };

      await updateFileApi({
        id: file.id,
        updates: {
          content: updatedContent,
          tags: pendingTags,
          metadata: updatedFile.metadata,
        },
      }).unwrap();

      dispatch(updateFile(updatedFile));
      setIsEditingTags(false);
    } catch (error) {
      // Reset tags on error
      setPendingTags(file.metadata.tags);
    }
  };

  const handleCancelTagEdit = () => {
    setPendingTags(file.metadata.tags);
    setIsEditingTags(false);
  };

  // Handle type change
  const handleTypeChange = async (newType: 'document' | 'phrase') => {
    if (!currentProject || newType === file.metadata.type) return;

    try {
      // Update the content with new frontmatter
      const updatedContent = updateFrontmatter(file.content, {
        type: newType,
      });

      const updatedFile = {
        ...file,
        content: updatedContent,
        metadata: {
          ...file.metadata,
          type: newType,
          updated: new Date().toISOString(),
        },
      };

      await updateFileApi({
        id: file.id,
        updates: {
          content: updatedContent,
          tags: pendingTags,
          metadata: updatedFile.metadata,
        },
      }).unwrap();

      dispatch(updateFile(updatedFile));
    } catch (error) {
      // TODO: Handle type update error
      console.error('Failed to update file type:', error);
    }
  };

  // Handle file duplication
  const handleDuplicate = async (newPath: string, newName: string) => {
    if (!currentProject) return;

    try {
      const duplicatedContent = updateFrontmatter(file.content, {
        title: file.metadata.title
          ? `${file.metadata.title} (Copy)`
          : undefined,
        updated: new Date().toISOString(),
      });

      const duplicatedFile = {
        ...file,
        id: `${Date.now()}-${Math.random()}`, // Temporary ID
        name: newName,
        path: newPath,
        content: duplicatedContent,
        metadata: {
          ...file.metadata,
          title: file.metadata.title
            ? `${file.metadata.title} (Copy)`
            : undefined,
          updated: new Date().toISOString(),
        },
      };

      await updateFileApi({
        id: duplicatedFile.id,
        updates: {
          name: newName,
          content: duplicatedContent,
          metadata: duplicatedFile.metadata,
        },
      }).unwrap();

      setDuplicateDialogOpen(false);
    } catch (error) {
      throw error;
    }
  };

  // Handle file rename
  const handleRename = async (newPath: string, newName: string) => {
    if (!currentProject) return;

    try {
      // First create the new file with a new name
      await updateFileApi({
        id: file.id,
        updates: {
          name: newName,
          content: file.content,
          metadata: file.metadata,
        },
      }).unwrap();

      // Then delete the old file
      await deleteFileApi(file.id).unwrap();

      const renamedFile = {
        ...file,
        name: newName,
        path: newPath,
      };

      dispatch(updateFile(renamedFile));
      setRenameDialogOpen(false);
    } catch (error) {
      throw error;
    }
  };

  // Handle file deletion
  const handleDelete = async () => {
    if (!currentProject) return;

    try {
      await deleteFileApi(file.id).unwrap();

      dispatch(deleteFileFromStore(file.id));
      setDeleteDialogOpen(false);
    } catch (error) {
      throw error;
    }
  };

  // Check if file has dependencies
  const fileDependencies = useMemo(() => {
    const referencingFiles = files.filter(
      (f) =>
        f.content.includes(`@${file.path}`) ||
        f.content.includes(`@${file.name}`)
    );
    return referencingFiles;
  }, [files, file]);

  // Handle revision restoration
  const handleRestoreRevision = async (
    _revisionId: string,
    content: string
  ) => {
    if (!currentProject) return;

    try {
      // Update the file with the restored content
      const restoredFile = {
        ...file,
        content,
        metadata: {
          ...file.metadata,
          updated: new Date().toISOString(),
        },
      };

      await updateFileApi({
        id: file.id,
        updates: {
          content,
          metadata: restoredFile.metadata,
        },
      }).unwrap();

      dispatch(updateFile(restoredFile));

      // Notify parent component about content change
      if (onContentChange) {
        onContentChange(content);
      }
    } catch (error) {
      throw error;
    }
  };

  const FileIcon = file.metadata.type === 'phrase' ? Hash : FileText;
  const lastModified = formatDistanceToNow(new Date(file.updatedAt), {
    addSuffix: true,
  });

  return (
    <>
      <div className="flex items-center justify-between border-b px-4 py-3 bg-background">
        {/* File metadata section */}
        <div className="flex items-center gap-4 min-w-0 flex-1">
          {/* File icon and basic info */}
          <div className="flex items-center gap-2 min-w-0">
            <FileIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-medium truncate text-sm">
                  {file.metadata.title || file.name}
                </h3>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(
                        'h-6 px-2 text-xs gap-1',
                        file.metadata.type === 'phrase'
                          ? 'text-blue-700 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'
                          : 'text-green-700 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300'
                      )}
                    >
                      {file.metadata.type === 'phrase' ? (
                        <Hash className="h-3 w-3" />
                      ) : (
                        <FileText className="h-3 w-3" />
                      )}
                      {file.metadata.type}
                      <FileType className="h-3 w-3 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuLabel>File Type</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuRadioGroup
                      value={file.metadata.type}
                      onValueChange={(value) =>
                        handleTypeChange(value as 'document' | 'phrase')
                      }
                    >
                      <DropdownMenuRadioItem value="document">
                        <FileText className="h-4 w-4 mr-2 text-green-600" />
                        Document
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="phrase">
                        <Hash className="h-4 w-4 mr-2 text-blue-600" />
                        Phrase
                      </DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <FolderOpen className="h-3 w-3" />
                <span className="truncate max-w-[200px]">{file.path}</span>
                <span>•</span>
                <Clock className="h-3 w-3" />
                <span>{lastModified}</span>
              </div>
            </div>
          </div>

          {/* Tags section */}
          <div className="flex items-center gap-2">
            {isEditingTags ? (
              <div className="flex items-center gap-2">
                <TagEditor
                  tags={pendingTags}
                  onChange={setPendingTags}
                  availableTags={allTags}
                  placeholder="Add tags..."
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleSaveTags}
                  disabled={isUpdating}
                  className="h-7 px-2"
                >
                  <Check className="h-3 w-3" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleCancelTagEdit}
                  className="h-7 px-2"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {file.metadata.tags.length > 0 ? (
                  <>
                    <Tag className="h-3 w-3 text-muted-foreground" />
                    <div className="flex gap-1">
                      {file.metadata.tags.map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Tag className="h-3 w-3" />
                    <span>No tags</span>
                  </div>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsEditingTags(true)}
                  className="h-7 px-2 text-xs"
                >
                  Edit
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Actions section */}
        <div className="flex items-center gap-2">
          {/* ALWAYS RENDER TOOLTIP PROVIDER TO AVOID HOOKS VIOLATIONS */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="default"
                  onClick={onSave}
                  className="h-8"
                  style={{ display: isDirty && onSave ? 'flex' : 'none' }}
                >
                  <Save className="h-3 w-3 mr-1" />
                  Save
                </Button>
              </TooltipTrigger>
              <TooltipContent>Save changes</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {isDirty && onSave && (
            <Separator orientation="vertical" className="h-6" />
          )}

          {/* File operations */}
          <div className="flex items-center">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setDuplicateDialogOpen(true)}
                    className="h-8"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Duplicate file</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setRenameDialogOpen(true)}
                    className="h-8"
                  >
                    <Edit3 className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Rename file</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <RevisionHistoryDialog
              file={file}
              onRestoreRevision={handleRestoreRevision}
            >
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8"
                      aria-label="View revision history"
                    >
                      <History className="h-3 w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>View revision history</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </RevisionHistoryDialog>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setDeleteDialogOpen(true)}
                    className="h-8 text-destructive hover:text-destructive"
                    disabled={isDeleting}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Delete file</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* More options dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8"
                  aria-label="More options"
                >
                  <MoreHorizontal className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => setDuplicateDialogOpen(true)}>
                  <Copy className="h-4 w-4 mr-2" />
                  Duplicate
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setRenameDialogOpen(true)}>
                  <Edit3 className="h-4 w-4 mr-2" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <RevisionHistoryDialog
                  file={file}
                  onRestoreRevision={handleRestoreRevision}
                >
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                    <History className="h-4 w-4 mr-2" />
                    View History
                  </DropdownMenuItem>
                </RevisionHistoryDialog>
                <DropdownMenuSeparator />
                <FrontMatterEditor
                  content={file.content}
                  onSave={async (newContent) => {
                    if (!currentProject) return;

                    const updatedFile = {
                      ...file,
                      content: newContent,
                      metadata: {
                        ...file.metadata,
                        updated: new Date().toISOString(),
                      },
                    };

                    await updateFileApi({
                      id: file.id,
                      updates: {
                        content: newContent,
                        metadata: updatedFile.metadata,
                      },
                    }).unwrap();

                    dispatch(updateFile(updatedFile));
                    if (onContentChange) {
                      onContentChange(newContent);
                    }
                  }}
                >
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                    <FileType className="h-4 w-4 mr-2" />
                    Edit Frontmatter
                  </DropdownMenuItem>
                </FrontMatterEditor>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setDeleteDialogOpen(true)}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Warning for files with dependencies */}
      {fileDependencies.length > 0 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 px-4 py-2">
          <div className="flex items-center">
            <AlertTriangle className="h-4 w-4 text-yellow-600 mr-2" />
            <p className="text-sm text-yellow-700">
              This file is referenced by {fileDependencies.length} other file
              {fileDependencies.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      )}

      {/* File operation dialogs */}
      <DuplicateFileDialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        onDuplicate={handleDuplicate}
        originalFile={file}
      />

      <RenameFileDialog
        open={renameDialogOpen}
        onClose={() => setRenameDialogOpen(false)}
        onRename={handleRename}
        originalFile={file}
      />

      <DeleteFileDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDelete={handleDelete}
        file={file}
        dependencies={fileDependencies}
      />
    </>
  );
}
